import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, Upload, Eye, Download, Pencil, Trash2, FileText, Image as ImageIcon, Loader2 } from "lucide-react";

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editName, setEditName] = useState("");
  const fileRef = useRef();

  const load = () => api.get("/certificates").then((r) => setCerts(r.data));
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("name", f.name.replace(/\.[^.]+$/, ""));
        await api.post("/certificates", fd);
      }
      toast.success(`${files.length} certificate(s) uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveName = async () => {
    if (!editName.trim()) return toast.error("Name required");
    await api.put(`/certificates/${edit.id}`, { name: editName });
    toast.success("Renamed");
    setEdit(null);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/certificates/${id}`);
    toast.success("Certificate removed");
    load();
  };

  const fileUrl = (id) => `${API}/certificates/${id}/file`;

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-slate-900" />
          </div>
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Certificate Library</h1>
            <p className="text-sm text-slate-500">Upload your standard certificates once, attach them to any bid.</p>
          </div>
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple hidden onChange={onFiles} data-testid="cert-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="upload-cert-btn">
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload Certificate
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {certs.map((c) => (
          <Card key={c.id} data-testid={`cert-${c.id}`} className="p-5 rounded-xl border-border group">
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${c.ext === "pdf" ? "bg-red-50" : "bg-blue-50"}`}>
                {c.ext === "pdf" ? <FileText className="h-5 w-5 text-red-500" /> : <ImageIcon className="h-5 w-5 text-blue-500" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-head font-bold truncate">{c.name}</div>
                <div className="text-[11px] text-slate-400 uppercase mt-0.5">{c.ext} · {(c.size / 1024).toFixed(0)} KB</div>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-4 pt-4 border-t border-border">
              <a href={fileUrl(c.id)} target="_blank" rel="noreferrer" data-testid={`cert-view-${c.id}`}>
                <Button size="sm" variant="ghost" className="h-8 rounded-lg text-slate-600"><Eye className="h-4 w-4 mr-1" /> View</Button>
              </a>
              <a href={fileUrl(c.id)} download data-testid={`cert-download-${c.id}`}>
                <Button size="sm" variant="ghost" className="h-8 rounded-lg text-slate-600"><Download className="h-4 w-4" /></Button>
              </a>
              <Button size="sm" variant="ghost" className="h-8 rounded-lg text-slate-600" onClick={() => { setEdit(c); setEditName(c.name); }} data-testid={`cert-rename-${c.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="h-8 rounded-lg text-red-600 ml-auto" onClick={() => remove(c.id)} data-testid={`cert-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
        {certs.length === 0 && <div className="text-sm text-slate-500 col-span-full py-14 text-center">No certificates yet. Upload PDF or image files to build your library.</div>}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename Certificate</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Certificate Name</Label>
            <Input data-testid="cert-name-input" value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-lg">Cancel</Button>
            <Button onClick={saveName} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="cert-save-name-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
